import { useEffect, useRef } from 'react';
import { type EventSourceMessage } from '@microsoft/fetch-event-source';
import { MercureClient } from '@/services/mercure/MercureClient';

interface IUseMercureSubscriptionParams {
  topics: readonly string[];
  onMessage: (message: EventSourceMessage) => void;
  enabled: boolean;
}

/**
 * Базовый переиспользуемый хук подписки на Mercure (SSE).
 * Создаёт один экземпляр MercureClient на время жизни хука и управляет его
 * запуском/остановкой через флаг `enabled`. Логику событий не содержит —
 * прокидывает сырые сообщения в переданный `onMessage`.
 *
 * Список топиков фиксируется на момент монтирования (рассчитан на стабильный
 * массив-константу). `onMessage` можно менять между рендерами — он берётся
 * через ref и не пересоздаёт соединение.
 *
 * @param params.topics - список топиков для подписки
 * @param params.onMessage - обработчик сырого сообщения Mercure
 * @param params.enabled - запускать подписку только когда `true`
 */
export const useMercureSubscription = ({
  topics,
  onMessage,
  enabled
}: IUseMercureSubscriptionParams): void => {
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const clientRef = useRef<MercureClient | null>(null);

  if (clientRef.current === null) {
    clientRef.current = new MercureClient({
      topics,
      onMessage: (message) => {
        onMessageRef.current(message);
      }
    });
  }

  useEffect(() => {
    const client = clientRef.current;

    if (client === null) {
      return;
    }

    if (enabled) {
      client.start();
    }

    return () => {
      client.stop();
    };
  }, [enabled]);
};
